// UPost.com - Upgrade to Pro Page
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { SUBSCRIPTION_PRICES } from '../config';

export function Upgrade() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  async function handleUpgrade(packageId) {
    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await api.request('/payments/checkout/session', {
        method: 'POST',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: originUrl
        })
      });

      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  const currentTier = user?.subscription_tier || 'free';
  
  if (currentTier === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-600" />
              You're on the Admin Plan!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">You have unlimited AI words, 10 collaborator seats, and all premium features.</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">
            {currentTier === 'free' ? 'Choose Your Plan' : 'Upgrade Your Plan'}
          </h1>
          <p className="text-xl text-gray-700">
            {currentTier === 'free' 
              ? 'Unlock unlimited AI writing and collaboration features'
              : 'Get more seats and advanced features'}
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Pro Plan - Show only for free users */}
          {currentTier === 'free' && (
            <Card className="border-2 border-purple-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                POPULAR
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">⚡</span>
                  Pro
                </CardTitle>
                <CardDescription>For serious writers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  ${SUBSCRIPTION_PRICES.pro_monthly}
                  <span className="text-base font-normal text-gray-600">/month</span>
                </div>
                <p className="text-sm text-green-600 font-semibold mb-6">
                  or ${SUBSCRIPTION_PRICES.pro_yearly}/year (save 20%)
                </p>
                
                <ul className="space-y-2 mb-6 min-h-[200px]">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Unlimited AI words
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Screenplay editor
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Priority AI processing
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Export to PDF
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Version history
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    No branding on exports
                  </li>
                </ul>

                <Button 
                  onClick={() => handleUpgrade('pro_monthly')}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Upgrade to Pro'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Team Plan - Show for free and pro users */}
          {(currentTier === 'free' || currentTier === 'pro') && (
            <Card className={`border-2 ${currentTier === 'pro' ? 'border-purple-500' : 'border-gray-200'} relative`}>
              {currentTier === 'pro' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  RECOMMENDED
                </div>
              )}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">👥</span>
                  Team
                </CardTitle>
                <CardDescription>Collaborate with your team</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  ${SUBSCRIPTION_PRICES.team_monthly}
                  <span className="text-base font-normal text-gray-600">/month</span>
                </div>
                <p className="text-sm text-green-600 font-semibold mb-6">
                  or ${SUBSCRIPTION_PRICES.team_yearly}/year (save 20%)
                </p>
                
                <ul className="space-y-2 mb-6 min-h-[200px]">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Everything in Pro, plus:
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    <strong>3 collaborator seats</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Real-time collaboration
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Comment & editing roles
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Team document sharing
                  </li>
                </ul>

                <Button 
                  onClick={() => handleUpgrade('team_monthly')}
                  disabled={loading || currentTier === 'team'}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Upgrade to Team'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Admin Plan - Show for free, pro, and team users */}
          {(currentTier === 'free' || currentTier === 'pro' || currentTier === 'team') && (
            <Card className={`border-2 ${currentTier === 'team' ? 'border-purple-500' : 'border-blue-500'} relative`}>
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                {currentTier === 'team' ? 'RECOMMENDED' : 'BEST VALUE'}
              </div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">👑</span>
                  Admin
                </CardTitle>
                <CardDescription>For professional teams</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  ${SUBSCRIPTION_PRICES.admin_monthly}
                  <span className="text-base font-normal text-gray-600">/month</span>
                </div>
                <p className="text-sm text-green-600 font-semibold mb-6">
                  or ${SUBSCRIPTION_PRICES.admin_yearly}/year (save 20%)
                </p>
                
                <ul className="space-y-2 mb-6 min-h-[200px]">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Everything in Team, plus:
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    <strong>10 collaborator seats</strong>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Admin analytics dashboard
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Team management tools
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="text-green-600" size={16} />
                    Priority support
                  </li>
                </ul>

                <Button 
                  onClick={() => handleUpgrade('admin_monthly')}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={16} />
                      Processing...
                    </>
                  ) : (
                    'Upgrade to Admin'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="text-center mt-8 text-sm text-gray-600">
          <p>Secure payment powered by Stripe</p>
          <p className="mt-2">Cancel anytime, no questions asked</p>
          <p className="mt-4">
            <a href="/pricing" className="text-purple-600 hover:text-purple-700 font-semibold underline">
              Compare all features & plans →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}